import { Fragment, useEffect, useRef, useState } from "react";
import {
  Menu,
  MenuButton,
  MenuItems,
  MenuItem,
  Transition,
} from "@headlessui/react";
import { cn } from "@utils/Cn";

// Dropdown Component
export function Dropdown({ children, className }) {
  return (
    <Menu as="div" className={cn("relative", className)}>
      {children}
    </Menu>
  );
}

// DropdownButton Component
export function DropdownButton({ children, className }) {
  return (
    <MenuButton as="div" className={cn(className)} role="button">
      {children}
    </MenuButton>
  );
}

// DropdownBody Component
export function DropdownBody({ children, position = "right", className }) {
  const containerRef = useRef(null);
  const [above, setAbove] = useState(false);

  useEffect(() => {
    const el = containerRef.current?.parentElement;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setAbove(rect.top > window.innerHeight / 2);
  });

  return (
    <div ref={containerRef}>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <MenuItems
          className={cn(
            "absolute z-30 w-56 rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none p-1 overflow-y-auto",
            above ? "bottom-full mb-2 origin-bottom" : "top-full mt-2 origin-top",
            position === "left" ? "right-0" : "left-0",
            className
          )}
        >
          {children}
        </MenuItems>
      </Transition>
    </div>
  );
}

// DropdownItem Component
export function DropdownItem({
  children,
  onClick,
  closeOnComplete,
  unstyled,
  disabled,
  className,
}) {
  return (
    <MenuItem as="div" className="relative">
      <div
        role="button"
        onClick={(e) => {
          if (!closeOnComplete) {
            e.preventDefault();
          }
          if (onClick && !disabled) onClick(e);
        }}
        className={cn(
          !unstyled &&
            "flex items-center rounded px-4 py-2 text-sm text-zinc-700 hover:text-zinc-900 focus:bg-zinc-100 focus:text-zinc-900",
          disabled ? "cursor-default" : "hover:bg-zinc-100",
          className
        )}
      >
        {children}
      </div>
    </MenuItem>
  );
}

// EmptyDropdownItem Component
export function EmptyDropdownItem({ children }) {
  return (
    <MenuItem as="div" className="relative" onClick={(e) => e.preventDefault()}>
      {children}
    </MenuItem>
  );
}

// Controlled components
export function DropdownControlled({ children, className, isOpen, setIsOpen }) {
  useEffect(() => {
    function handleClickOutside(event) {
      if (isOpen && !event.target.closest(".relative")) {
        // TODO - Something better than a generic relative class
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, setIsOpen]);

  return (
    <Menu as="div" className={cn("relative", className)}>
      {children}
    </Menu>
  );
}

export function DropdownBodyControlled({
  children,
  position = "right",
  className,
  isOpen,
  ...rest
}) {
  return (
    <Transition
      as={Fragment}
      show={isOpen}
      enter="transition ease-out duration-100"
      enterFrom="transform opacity-0 scale-95"
      enterTo="transform opacity-100 scale-100"
      leave="transition ease-in duration-75"
      leaveFrom="transform opacity-100 scale-100"
      leaveTo="transform opacity-0 scale-95"
    >
      <MenuItems
        static
        className={cn(
          "absolute z-30 mt-2 min-w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none",
          position === "left" ? "right-0" : "left-0",
          className
        )}
        {...rest}
      >
        {children}
      </MenuItems>
    </Transition>
  );
}
